import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { appointmentsAPI } from '../api';
import toast from 'react-hot-toast';
import { Search, Eye, ChevronLeft, ChevronRight } from 'lucide-react';

const StatusBadge = ({ status }) => {
    const map = {
        Accepted: 'badge-success',
        Completed: 'badge-success',
        Pending: 'badge-warning',
        Assigned: 'badge-info',
        Cancelled: 'badge-danger',
        Timeout: 'badge-danger',
    };
    return (
        <span className={`badge ${map[status] || 'badge-gray'}`}>{status}</span>
    );
};

export default function Appointments() {
    const navigate = useNavigate();
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState('');
    const [type, setType] = useState('');
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const limit = 10;

    const fetchAppointments = () => {
        setLoading(true);
        appointmentsAPI
            .getAll({ page, limit, search, status, type })
            .then((res) => {
                setAppointments(res.data.data.appointments);
                setTotal(res.data.data.total);
            })
            .catch(() => toast.error('Failed to load'))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchAppointments();
    }, [page, status, type]);
    useEffect(() => {
        const t = setTimeout(() => {
            setPage(1);
            fetchAppointments();
        }, 400);
        return () => clearTimeout(t);
    }, [search]);

    const pages = Math.ceil(total / limit);

    return (
        <div className="fade-in">
            <div className="page-header">
                <div>
                    <h1 className="page-title">Appointments</h1>
                    <p className="page-subtitle">{total} total appointments</p>
                </div>
            </div>

            <div className="filters">
                <div className="search-bar" style={{ flex: 1, maxWidth: 300 }}>
                    <Search size={16} />
                    <input
                        className="input"
                        placeholder="Search patient or doctor..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                <select
                    className="input"
                    value={status}
                    onChange={(e) => {
                        setStatus(e.target.value);
                        setPage(1);
                    }}
                >
                    <option value="">All Status</option>
                    <option value="Pending">Pending</option>
                    <option value="Assigned">Assigned</option>
                    <option value="Accepted">Accepted</option>
                    <option value="Completed">Completed</option>
                    <option value="Cancelled">Cancelled</option>
                    <option value="Timeout">Timeout</option>
                </select>
                <select
                    className="input"
                    value={type}
                    onChange={(e) => {
                        setType(e.target.value);
                        setPage(1);
                    }}
                >
                    <option value="">All Types</option>
                    <option value="Home">Home Visit</option>
                    <option value="Online">Online</option>
                </select>
            </div>

            <div className="card" style={{ padding: 0 }}>
                <div className="table-wrapper">
                    <table>
                        <thead>
                            <tr>
                                <th>Patient</th>
                                <th>Doctor</th>
                                <th>Type</th>
                                <th>Date & Time</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={7}>
                                        <div className="loading-container">
                                            <div className="spinner" />
                                        </div>
                                    </td>
                                </tr>
                            ) : appointments.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={7}
                                        style={{
                                            textAlign: 'center',
                                            color: 'var(--text3)',
                                            padding: 60,
                                        }}
                                    >
                                        No appointments found
                                    </td>
                                </tr>
                            ) : (
                                appointments.map((a) => (
                                    <tr key={a.appointment_id}>
                                        <td>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 10,
                                                }}
                                            >
                                                <div
                                                    className="avatar"
                                                    style={{
                                                        width: 32,
                                                        height: 32,
                                                        fontSize: 12,
                                                    }}
                                                >
                                                    {(a.patient_name || 'P')[0]}
                                                </div>
                                                <span
                                                    style={{
                                                        color: 'var(--text)',
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    {a.patient_name || '—'}
                                                </span>
                                            </div>
                                        </td>
                                        <td>
                                            <div
                                                style={{
                                                    fontWeight: 500,
                                                    color: 'var(--text)',
                                                }}
                                            >
                                                {a.doctor_name || '—'}
                                            </div>
                                            <div
                                                style={{
                                                    fontSize: 12,
                                                    color: 'var(--text3)',
                                                }}
                                            >
                                                {a.specialization}
                                            </div>
                                        </td>
                                        <td>
                                            <span className="tag">
                                                {a.appointment_type}
                                            </span>
                                        </td>
                                     
                                        <td style={{ fontSize: 13 }}>
                                            {a.appointment_date}
                                            <br />
                                            <span
                                                style={{
                                                    color: 'var(--text3)',
                                                }}
                                            >
                                                {a.appointment_time}
                                            </span>
                                        </td>
                                        <td>
                                            <StatusBadge status={a.status} />
                                        </td>
                                        <td>
                                            <button
                                                className="btn btn-ghost btn-sm"
                                                onClick={() =>
                                                    navigate(
                                                        `/appointments/${a.appointment_id}`
                                                    )
                                                }
                                            >
                                                <Eye size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {pages > 1 && (
                    <div
                        style={{
                            padding: '16px 20px',
                            borderTop: '1px solid var(--border)',
                        }}
                    >
                        <div className="pagination">
                            <button
                                className="page-btn"
                                onClick={() => setPage((p) => p - 1)}
                                disabled={page === 1}
                            >
                                <ChevronLeft size={14} />
                            </button>
                            {Array.from(
                                { length: Math.min(pages, 5) },
                                (_, i) => i + 1
                            ).map((p) => (
                                <button
                                    key={p}
                                    className={`page-btn ${page === p ? 'active' : ''}`}
                                    onClick={() => setPage(p)}
                                >
                                    {p}
                                </button>
                            ))}
                            <button
                                className="page-btn"
                                onClick={() => setPage((p) => p + 1)}
                                disabled={page === pages}
                            >
                                <ChevronRight size={14} />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
